import type {
  ContractFamilyContactChange,
  SupplierContactMode,
  SupplierContactsPreview,
  SupplierContactsRequest,
} from "@/types/supplier";

interface FamilyContactValues {
  mode: SupplierContactMode;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

export interface ContactsFormValues {
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_notes: string;
  families: Record<string, FamilyContactValues>;
}

const text = (value: string | null | undefined) => value ?? "";
const optional = (value: string) => value.trim() || null;

export const toFormValues = (preview: SupplierContactsPreview): ContactsFormValues => ({
  contact_name: text(preview.contact_name),
  contact_email: text(preview.contact_email),
  contact_phone: text(preview.contact_phone),
  contact_notes: text(preview.contact_notes),
  families: Object.fromEntries(
    preview.families.map(family => [
      family.relation_id,
      {
        mode: family.mode,
        contact_name: text(family.contact_name),
        contact_email: text(family.contact_email),
        contact_phone: text(family.contact_phone),
      },
    ]),
  ),
});

export const buildContactsRequest = (
  preview: SupplierContactsPreview,
  values: ContactsFormValues,
): SupplierContactsRequest => {
  const family_changes: ContractFamilyContactChange[] = [];
  for (const family of preview.families) {
    const current = values.families[family.relation_id];
    if (!current || !family.can_edit) continue;
    const changed =
      current.mode !== family.mode ||
      (current.mode === "custom" &&
        (optional(current.contact_name) !== family.contact_name ||
          optional(current.contact_email) !== family.contact_email ||
          optional(current.contact_phone) !== family.contact_phone));
    if (!changed) continue;
    if (current.mode === "inherited") {
      family_changes.push({ relation_id: family.relation_id, mode: current.mode });
      continue;
    }
    family_changes.push({
      relation_id: family.relation_id,
      mode: current.mode,
      contact_name: optional(current.contact_name),
      contact_email: optional(current.contact_email),
      contact_phone: optional(current.contact_phone),
    });
  }
  return {
    contact_name: optional(values.contact_name),
    contact_email: optional(values.contact_email),
    contact_phone: optional(values.contact_phone),
    contact_notes: optional(values.contact_notes),
    family_changes,
  };
};
